<script setup>
import { RefreshIcon } from "@heroicons/vue/outline";
import { ref } from "vue";
import { computed, defineProps, defineEmits, watch } from "vue";

const props = defineProps({
  size: {
    type: String,
    default: "md",
    validate: (value) => ["sm", "md", "lg"].includes(value),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingText: {
    type: String,
    default: "Loading...",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  outline: {
    type: Boolean,
    default: false,
  },
  backgroundColor: {
    type: String,
    default: "",
  },
  textColor: {
    type: String,
    default: "",
  },
  label: {
    type: String,
    default: "",
  },
  url: {
    type: String,
    default: "",
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  openInNewTab: {
    type: Boolean,
    default: false,
  },
  rounded: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["click"]);

const refreshKey = ref(0)

const loading = computed(() => props.loading);
const loadingText = computed(() => props.loadingText);
const size = computed(() => props.size);
const disabled = computed(() => props.disabled);
const backgroundColor = computed(() => props.backgroundColor);
const url = computed(() => props.url);
const label = computed(() => props.label);
const textColor = computed(() => props.textColor);
const openInNewTab = computed(() => props.openInNewTab);

const handleClick = (event) => {
  if (!disabled.value && !loading.value) {
    emit("click", event);
  }
};

watch([backgroundColor, textColor], () => {
  refreshKey.value++
})
</script>
<template>
  <a
    v-bind="$attrs"
    :href="url"
    :key="refreshKey"
    :style="{
      backgroundColor: backgroundColor,
      color: textColor,
    }"
    :class="{
      // end of outline styles
      'px-4 lg:px-6 py-2.5 lg:py-3 text-sm font-normal': size.toLowerCase() === 'lg',
      'px-4 lg:px-6 py-1.5 lg:py-2 text-sm font-normal': size.toLowerCase() === 'md',
      'px-3 lg:px-4 lg:py-1.5 py-1 text-xs font-normal': size.toLowerCase() === 'sm',
      'cursor-not-allowed bg-kintz-primary-4/30': loading || disabled,
      'w-full': fullWidth,
      'rounded-xl' : rounded && size.toLowerCase() === 'lg',
      'rounded-lg' : rounded && size.toLowerCase() === 'md',
      'rounded-md' : rounded && size.toLowerCase() === 'sm',
      'w-fit': !fullWidth,
    }"
    class="flex gap-1 lg:gap-2 justify-center items-center max-h-12 truncate h-fit w-fit"
    :disabled="loading || disabled"
    @click="handleClick"
    :target="openInNewTab ? '_blank' : '_self'"
  >
    <RefreshIcon
      v-if="loading"
      class="animate-spin-reverse h-5 w-5"
      aria-hidden="true"
    />
    <slot v-if="!loading">
      {{ label }}
    </slot>
    <span v-if="loading">{{ loadingText }}</span>
  </a>
</template>

<style scoped></style>
